import { useEffect } from 'react';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { Routes, Route, useLocation } from 'react-router-dom';

import { NAV_LINKS } from './lib/navigation';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { FloatingContactButton } from './components/FloatingContactButton';
import { LocaleProvider } from './i18n';
import { useTranslations, type Locale } from './i18n';
import {
  buildAlternateLinks,
  buildCanonicalUrl,
  buildStructuredData,
  getOgLocale,
  SITE_NAME,
  CONTACT_EMAIL,
  LOGO_URL,
  resolveRouteKey,
} from './lib/seo';

import { HomePage } from './pages/HomePage';
import { ContactPage } from './pages/ContactPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';
import { AboutPage } from './pages/AboutPage';
import { MenuPage } from './pages/MenuPage';
import { GalleryPage } from './pages/GalleryPage';

function AppContent() {
  const location = useLocation();
  const { t, locale } = useTranslations();
  const routeKey = resolveRouteKey(location.pathname);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    let frameOne = 0;
    let frameTwo = 0;

    if (location.hash) {
      const targetId = location.hash.slice(1);

      const scrollToTarget = () => {
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };

      frameOne = window.requestAnimationFrame(() => {
        frameTwo = window.requestAnimationFrame(scrollToTarget);
      });
    } else {
      window.scrollTo({ top: 0 });
    }

    return () => {
      if (frameOne) {
        window.cancelAnimationFrame(frameOne);
      }
      if (frameTwo) {
        window.cancelAnimationFrame(frameTwo);
      }
    };
  }, [location.pathname, location.hash]);

  const defaultTitle = t('meta.title', 'CheckPoint Charly | Jazz Restaurant in Sofia') as string;
  const defaultDescription = t(
    'meta.description',
    'Experience CheckPoint Charly in downtown Sofia — contemporary cuisine, curated wines, and live jazz.',
  ) as string;
  const title = (t(`meta.routes.${routeKey}.title`, defaultTitle) as string) ?? defaultTitle;
  const description = (t(`meta.routes.${routeKey}.description`, defaultDescription) as string) ?? defaultDescription;
  const canonicalUrl = buildCanonicalUrl(location.pathname, locale as Locale);
  const structuredData = buildStructuredData(locale as Locale, canonicalUrl, title, description);
  const alternateLinks = buildAlternateLinks(location.pathname);
  const ogLocale = getOgLocale(locale as Locale);
  const defaultAlternate = alternateLinks.find((link) => link.hrefLang === 'x-default');

  const hrefLangMeta = alternateLinks
    .filter((link) => link.hrefLang !== 'x-default')
    .map((link) => (
      <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
    ));

  return (
    <div className="app-shell min-h-screen bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] transition-colors">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        {hrefLangMeta}
        {defaultAlternate ? (
          <link rel="alternate" hrefLang="x-default" href={defaultAlternate.href} />
        ) : null}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={LOGO_URL} />
        <meta property="og:locale" content={ogLocale} />
        <meta property="og:locale:alternate" content={locale === 'bg' ? 'en_US' : 'bg_BG'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={LOGO_URL} />
        <meta name="twitter:site" content="@checkpointcharly" />
        <meta name="twitter:creator" content="@checkpointcharly" />
        <meta name="generator" content="Vite" />
        <meta name="author" content={SITE_NAME} />
        <meta name="robots" content="index, follow" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="theme-color" content="#0f172a" />
        <script type="application/ld+json">{structuredData}</script>
        <meta name="copyright" content={`© ${new Date().getFullYear()} ${SITE_NAME}`} />
        <link rel="me" href={`mailto:${CONTACT_EMAIL}`} />
        <meta name="application-name" content={SITE_NAME} />
        <meta name="apple-mobile-web-app-title" content={SITE_NAME} />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="msapplication-TileColor" content="#0f172a" />
        <meta property="og:image:alt" content={`${SITE_NAME} logo`} />
        <meta property="og:image:type" content="image/svg+xml" />
        <meta property="og:image:width" content="240" />
        <meta property="og:image:height" content="240" />
        <meta name="twitter:image:alt" content={`${SITE_NAME} logo`} />
        <meta name="contact" content={CONTACT_EMAIL} />
        <meta name="publisher" content={SITE_NAME} />
        <meta name="language" content={locale === 'bg' ? 'bg-BG' : 'en-US'} />
        <meta name="distribution" content="global" />
        <meta name="referrer" content="no-referrer-when-downgrade" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta property="og:see_also" content="https://www.linkedin.com/company/everbranch" />
        <meta property="og:see_also" content="https://github.com/everbranch" />
        <meta name="google-site-verification" content="" />
      </Helmet>
      <div className="relative flex min-h-screen w-full flex-col">
        <Header links={NAV_LINKS} />
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy-policy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
          </Routes>
        </main>
        <Footer />
        <FloatingContactButton />
      </div>
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <LocaleProvider>
        <AppContent />
      </LocaleProvider>
    </HelmetProvider>
  );
}
